import type {
  BlindSpot,
  BlindSpotGroup,
  BlindSpotType,
  Divergence,
  InterviewPanel,
  Probe,
} from "@shared/labs/level-assessment-interview-panel";

/**
 * Pure derivation layer for the interview-panel page's 3-act layout.
 *
 * The server contract keeps blind spots, the probe script, and the divergence
 * quotes as three parallel arrays (they feed three independent build phases).
 * The page, however, tells ONE story per competency: "here is what's still
 * open, here is what the last interview suggested, here is how to clarify it."
 * This module joins the three arrays by axis into that story, and splits the
 * lifecycle states the contract folds together:
 *
 *   - OPEN   (status `open` / `partially_addressed`) → Act 2, "o que falta
 *     esclarecer", merged with the axis's probe and its a-verificar quotes.
 *   - SETTLED (status `addressed` / `refuted`)       → Act 3, "o que já foi
 *     esclarecido", a quiet history list — never rendered as a blind spot.
 *
 * Everything here is deterministic and side-effect free so it can be unit
 * tested against every contract vintage (legacy no-ledger, v4 per-point
 * resolution, v5 aggregated subPoints).
 */

/** Mirrors the assembler's synthetic axis for quotes/points with no axis. */
export const PROFILE_AXIS_ID = "profile-geral";
export const PROFILE_AXIS_LABEL = "Perfil geral";

/** Ledger statuses that still warrant interview time. `partially_addressed`
 *  stays open on purpose: a partial answer is a reason to probe again, not a
 *  closed record. */
const OPEN_STATUSES = new Set(["open", "partially_addressed"]);

export type OutcomeTone = "green" | "amber" | "muted";

/** A lifecycle statement rendered as a plain colored word (Word-Carries-State
 *  rule): no pill, no icon — the text and its canonical tone ARE the status. */
export interface StatusWord {
  text: string;
  tone: OutcomeTone;
}

const OUTCOME_WORD: Record<string, StatusWord> = {
  confirmed: { text: "confirmou o nível", tone: "green" },
  strengthened: { text: "fortaleceu a leitura", tone: "green" },
  weakened: { text: "revisou para baixo", tone: "amber" },
  inconclusive: { text: "inconclusivo", tone: "muted" },
};

function outcomeWord(outcome: string | null | undefined): StatusWord | null {
  return outcome ? (OUTCOME_WORD[outcome] ?? null) : null;
}

/** One still-open question inside a clarify card. */
export interface OpenItem {
  id: string;
  title: string;
  type: BlindSpotType;
  /** The ledger point's own suggested probe, when it carries one. */
  probe: string | null;
  /** Present only when the point was partially addressed by an earlier round:
   *  the interviewer should know it was touched, and with what outcome. */
  partial: { roundLabel: string | null; outcome: StatusWord | null } | null;
}

/** One merged Act-2 card: everything the interviewer needs about one axis. */
export interface ClarifyCard {
  rank: number;
  axisId: string;
  axisLabel: string;
  group: BlindSpotGroup;
  /** The most severe open gap type; null for probe-only / quote-only cards. */
  type: BlindSpotType | null;
  impactBadge: string;
  /** Legacy one-line framing (pre-v5 panels); "" when subPoints carry it. */
  body: string;
  detail: BlindSpot["detail"] | undefined;
  openItems: OpenItem[];
  probe: Probe | null;
  quotes: Divergence[];
}

/** One settled record for Act 3. */
export interface ResolvedItem {
  key: string;
  axisLabel: string;
  /** The point's own title; falls back to the axis label on v4 shapes. */
  title: string;
  roundLabel: string | null;
  word: StatusWord;
}

export interface PanelActs {
  open: ClarifyCard[];
  resolved: ResolvedItem[];
}

function resolvedWord(status: string, outcome: string | null | undefined): StatusWord {
  if (status === "refuted") return { text: "não corroborado", tone: "muted" };
  return outcomeWord(outcome) ?? { text: "endereçado", tone: "green" };
}

/** Which card a divergence quote belongs to. Grouped (S4 digest) quotes carry
 *  the axis directly; legacy Call-1 quotes point at a blind spot by label. */
function quoteAxisKey(d: Divergence, labelToAxisId: Map<string, string>): string {
  if (d.groupAxisId) return d.groupAxisId;
  if (d.groupAxisLabel) return labelToAxisId.get(d.groupAxisLabel) ?? d.groupAxisLabel;
  if (d.relatedBlindSpot) {
    return labelToAxisId.get(d.relatedBlindSpot.axisLabel) ?? d.relatedBlindSpot.axisLabel;
  }
  return PROFILE_AXIS_ID;
}

function quoteAxisLabel(d: Divergence): string {
  return d.groupAxisLabel ?? d.relatedBlindSpot?.axisLabel ?? PROFILE_AXIS_LABEL;
}

export function derivePanelActs(panel: InterviewPanel): PanelActs {
  const resolved: ResolvedItem[] = [];
  const cards: Omit<ClarifyCard, "rank">[] = [];
  const cardByAxis = new Map<string, Omit<ClarifyCard, "rank">>();

  const pushCard = (card: Omit<ClarifyCard, "rank">) => {
    cards.push(card);
    cardByAxis.set(card.axisId, card);
  };

  for (const spot of panel.blindSpots) {
    const subPoints = spot.subPoints ?? [];

    if (subPoints.length > 0) {
      // v5 aggregated card: split its points by lifecycle.
      const openItems: OpenItem[] = [];
      for (const sub of subPoints) {
        if (OPEN_STATUSES.has(sub.status)) {
          openItems.push({
            id: sub.id,
            title: sub.title,
            type: sub.type,
            probe: sub.probe,
            partial:
              sub.status === "partially_addressed"
                ? { roundLabel: sub.roundLabel, outcome: outcomeWord(sub.outcome) }
                : null,
          });
        } else {
          resolved.push({
            key: sub.id,
            axisLabel: spot.axisLabel,
            title: sub.title,
            roundLabel: sub.roundLabel,
            word: resolvedWord(sub.status, sub.outcome),
          });
        }
      }
      if (openItems.length > 0) {
        pushCard({
          axisId: spot.axisId,
          axisLabel: spot.axisLabel,
          group: spot.group,
          type: spot.type,
          impactBadge: spot.impactBadge,
          body: "",
          detail: undefined,
          openItems,
          probe: null,
          quotes: [],
        });
      }
      continue;
    }

    // v4 shape: one card per point, `resolution` present iff the point settled.
    if (spot.resolution && spot.resolution.status !== "partially_addressed") {
      resolved.push({
        key: `${spot.axisId}-${spot.rank}`,
        axisLabel: spot.axisLabel,
        title: spot.body || spot.axisLabel,
        roundLabel: spot.resolution.roundLabel,
        word: resolvedWord(spot.resolution.status, spot.resolution.outcome),
      });
      continue;
    }

    // Legacy / still-open single-point card.
    pushCard({
      axisId: spot.axisId,
      axisLabel: spot.axisLabel,
      group: spot.group,
      type: spot.type,
      impactBadge: spot.impactBadge,
      body: spot.body,
      detail: spot.detail,
      openItems: [],
      probe: null,
      quotes: [],
    });
  }

  // Join the probe script by axis; leftovers become probe-only cards.
  for (const probe of panel.script) {
    const card = cardByAxis.get(probe.axisId);
    if (card) {
      card.probe = probe;
    } else {
      pushCard({
        axisId: probe.axisId,
        axisLabel: probe.axisLabel,
        group: "disambiguation",
        type: null,
        impactBadge: "",
        body: "",
        detail: undefined,
        openItems: [],
        probe,
        quotes: [],
      });
    }
  }

  // Join the a-verificar quotes by axis; leftovers become quote-only cards.
  const labelToAxisId = new Map<string, string>();
  for (const card of cards) labelToAxisId.set(card.axisLabel, card.axisId);
  for (const d of panel.divergences) {
    const key = quoteAxisKey(d, labelToAxisId);
    const card = cardByAxis.get(key);
    if (card) {
      card.quotes.push(d);
    } else {
      pushCard({
        axisId: key,
        axisLabel: quoteAxisLabel(d),
        group: "disambiguation",
        type: null,
        impactBadge: "",
        body: "",
        detail: undefined,
        openItems: [],
        probe: null,
        quotes: [d],
      });
    }
  }

  return { open: cards.map((c, i) => ({ ...c, rank: i + 1 })), resolved };
}

/** Plain-text interview guide for the copy button — rebuilt client-side from
 *  the merged cards so it matches what the page actually shows (the server's
 *  `copyText` still carries the pre-merge "roteiro" framing). */
export function buildGuideText(panel: InterviewPanel, acts: PanelActs): string {
  const cl = panel.currentLevel;
  const levelLabel =
    cl.level === null ? "sem nível" : `L${cl.level}${cl.levelName ? ` · ${cl.levelName}` : ""}`;
  const lines = [
    `${panel.candidate.name} · ${panel.meta.modelName}`,
    `Rodada ${panel.meta.roundSequence}${panel.meta.roundLabel ? ` · ${panel.meta.roundLabel}` : ""}`,
    `Nível atual: ${levelLabel}`,
    "",
    "O que a entrevista precisa esclarecer:",
  ];
  if (acts.open.length === 0) {
    lines.push("  (nenhum ponto em aberto)");
  }
  for (const card of acts.open) {
    const boundary = card.probe ? ` (${card.probe.boundary.from} → ${card.probe.boundary.to})` : "";
    lines.push(`${card.rank}. ${card.axisLabel}${boundary}`);
    for (const item of card.openItems) lines.push(`   • ${item.title}`);
    if (card.body && card.openItems.length === 0) lines.push(`   ${card.body}`);
    if (card.probe) {
      for (const q of card.probe.suggestedQuestions) lines.push(`   - ${q}`);
    }
  }
  if (acts.resolved.length > 0) {
    lines.push("", "Já esclarecido em rodadas anteriores:");
    for (const r of acts.resolved) {
      const round = r.roundLabel ? ` (${r.roundLabel})` : "";
      lines.push(`  • ${r.axisLabel} · ${r.title}: ${r.word.text}${round}`);
    }
  }
  return lines.join("\n");
}
